import React from 'react';
import './StatisticsSection.css';

export const formatNumber = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '0';
  }
  return new Intl.NumberFormat('es-MX').format(numeric);
};

export const formatCurrency = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '$0 MXN';
  }
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(numeric);
};

export const formatPercent = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '0%';
  }
  return `${Math.round(numeric)}%`;
};

export function StatisticsSectionContainer({
  title,
  subtitle,
  periodLabel = 'Últimos 30 días',
  actions = null,
  children,
}) {
  return (
    <div className="statistics-root">
      <header className="statistics-header">
        <div>
          <h1>{title}</h1>
          {subtitle ? <p className="statistics-subtitle">{subtitle}</p> : null}
        </div>
        <div className="statistics-header-meta">
          {actions}
          {periodLabel ? (
            <button type="button" className="statistics-period-button">
              {periodLabel}
            </button>
          ) : null}
        </div>
      </header>
      {children}
    </div>
  );
}

export function StatisticsHighlights({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;

  const chunkSize = 4;
  const rows = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    rows.push(items.slice(index, index + chunkSize));
  }

  return (
    <div className="statistics-card-group">
      {rows.map((group, rowIndex) => (
        <div
          key={`statistics-row-${rowIndex}`}
          className={`statistics-card-row${rowIndex > 0 ? ' statistics-card-row--secondary' : ''}`}
        >
          {group.map((item, itemIndex) => {
            const {
              id,
              label,
              value,
              valueFormatted,
              delta,
              caption,
              trend,
              accent,
            } = item;
            const displayValue =
              valueFormatted ||
              (typeof value === 'number' && Number.isFinite(value)
                ? formatNumber(value)
                : value);
            const trendClass = trend ? ` is-${trend}` : '';
            const hasAccent = accent || (rowIndex === 0 && itemIndex === 0);
            const accentClass = hasAccent ? ' is-accent' : '';
            const wideClass = hasAccent && group.length > 3 ? ' statistics-card--wide' : '';

            return (
              <article
                key={id || `${label}-${itemIndex}`}
                className={`statistics-card${trendClass}${accentClass}${wideClass}`}
              >
                <span className="statistics-card-label">{label}</span>
                <span className="statistics-card-value">{displayValue}</span>
                {delta ? <span className="statistics-card-trend">{delta}</span> : null}
                {caption ? <span className="statistics-card-caption">{caption}</span> : null}
              </article>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export function StatisticsProgressList({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="statistics-progress-list">
      {items.map(item => {
        const {
          id,
          label,
          value = 0,
          displayValue,
          suffix = '%',
          caption,
          tone,
        } = item;
        const percent = Math.max(0, Math.min(100, Number(value) || 0));
        const fillClass =
          tone === 'warning'
            ? 'statistics-progress-bar-fill is-warning'
            : 'statistics-progress-bar-fill';
        const valueLabel =
          displayValue ?? `${Math.round(percent)}${suffix ?? ''}`;
        return (
          <div key={id || label} className="statistics-progress-item">
            <div className="statistics-progress-meta">
              <span>{label}</span>
              <span className="statistics-progress-value">{valueLabel}</span>
            </div>
            <div className="statistics-progress-bar" aria-hidden="true">
              <span className={fillClass} style={{ width: `${percent}%` }} />
            </div>
            {caption ? (
              <span className="statistics-progress-caption">{caption}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function StatisticsInsightList({ title, items, footer }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <article className="statistics-insights">
      {title ? <h2>{title}</h2> : null}
      <ul>
        {items.map((text, index) => (
          <li key={`${text}-${index}`}>{text}</li>
        ))}
      </ul>
      {footer ? <div className="statistics-progress-caption">{footer}</div> : null}
    </article>
  );
}

export function StatisticsPillGroup({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="statistics-pill-group">
      {items.map(item => (
        <div key={item.id || item.label} className="statistics-pill">
          {item.icon ? <span aria-hidden="true">{item.icon}</span> : null}
          <div>
            {item.label ? <span>{item.label}</span> : null}
            {item.value ? <strong>{item.value}</strong> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export function StatisticsKpiGrid({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="statistics-kpi-grid">
      {items.map(item => (
        <div key={item.id || item.label} className="statistics-kpi">
          <strong>{item.value}</strong>
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

const PLAN_CREDIT_CAPACITY = {
  elite: 320,
  premium: 240,
  pro: 160,
  founder: 120,
  free: 60,
  default: 140,
};

export const getPlanCreditCapacity = plan => {
  const slug = String(plan || '').toLowerCase();
  return PLAN_CREDIT_CAPACITY[slug] || PLAN_CREDIT_CAPACITY.default;
};

export const computePeriodDelta = (current, previous) => {
  if (!Number.isFinite(Number(current)) || !Number.isFinite(Number(previous))) {
    return null;
  }
  const safeCurrent = Number(current);
  const safePrevious = Number(previous);
  if (safePrevious <= 0 && safeCurrent <= 0) {
    return null;
  }
  if (safePrevious === safeCurrent) {
    return {
      direction: 'neutral',
      value: '•',
      caption: 'Sin variación',
    };
  }
  const diff = safeCurrent - safePrevious;
  const percent = safePrevious === 0 ? 100 : Math.round((Math.abs(diff) / safePrevious) * 100);
  return {
    direction: diff >= 0 ? 'positive' : 'negative',
    value: `${diff >= 0 ? '↑' : '↓'} ${percent}%`,
    caption: 'vs periodo anterior',
  };
};

export function AnalyticsDonut({ percent = 0, label, value, caption, tone = 'success' }) {
  const normalizedPercent = Math.max(0, Math.min(100, Number(percent) || 0));
  const displayPercent = Math.round(normalizedPercent);
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (normalizedPercent / 100) * circumference;

  return (
    <div className={`analytics-donut analytics-donut--${tone}`}>
      <div className="analytics-donut__ring">
        <svg viewBox="0 0 120 120" className="analytics-donut__svg" aria-hidden="true">
          <circle className="analytics-donut__track" cx="60" cy="60" r={radius} />
          <circle
            className="analytics-donut__progress"
            cx="60"
            cy="60"
            r={radius}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
          />
        </svg>
        <div className="analytics-donut__center">
          <span className="analytics-donut__value statistics-mono">{value}</span>
          <span className="analytics-donut__percent">{displayPercent}%</span>
        </div>
      </div>
      <div className="analytics-donut__meta">
        <span className="analytics-donut__label">{label}</span>
        {caption ? <p>{caption}</p> : null}
      </div>
    </div>
  );
}

export function AnalyticsGlyph({ variant = 'pulse' }) {
  switch (variant) {
    case 'radar':
      return (
        <svg viewBox="0 0 40 40" fill="none" aria-hidden="true">
          <circle cx="20" cy="20" r="18" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
          <path
            d="M20 4v16l10 10"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.7"
          />
        </svg>
      );
    case 'target':
      return (
        <svg viewBox="0 0 40 40" fill="none" aria-hidden="true">
          <circle cx="20" cy="20" r="14" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2" />
          <circle cx="20" cy="20" r="6" stroke="currentColor" strokeWidth="3" opacity="0.8" />
          <path
            d="M4 20h8M20 4v8M36 20h-8M20 36v-8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            opacity="0.4"
          />
        </svg>
      );
    case 'stack':
      return (
        <svg viewBox="0 0 40 40" fill="none" aria-hidden="true">
          <rect x="8" y="8" width="24" height="8" rx="2" stroke="currentColor" strokeWidth="2" opacity="0.6" />
          <rect x="8" y="18" width="24" height="8" rx="2" stroke="currentColor" strokeWidth="2" opacity="0.4" />
          <rect x="8" y="28" width="24" height="8" rx="2" stroke="currentColor" strokeWidth="2" opacity="0.2" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 40 40" fill="none" aria-hidden="true">
          <path
            d="M6 24c4-8 8-12 14-12s10 2 14 12"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.6"
          />
          <path
            d="M10 18l6 6 8-10"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.9"
          />
        </svg>
      );
  }
}
